import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";

import TailwindExpertResumeLayout from "../../components/ExpertResumesLayout/TailwindExpertResumeLayout";
import DataTable from "../../components/Opportunities/data-table";
import SearchInput from "../../components/Opportunities/search-input";
import StatisticsCard from "../../components/Opportunities/statistics-card";
import ViewOpportunityDrawer from "../../components/Opportunities/view-opportunity-drawer";
import { Icons } from "../../components/ui/icons";
import { cn } from "../../handlers/cn.utils";
import { getJobsRoutes } from "../../handlers/navigationItems";

function OpportunityDetails() {
	const location = useLocation();
	const [searchOpportunityTerm, setSearchOpportunityTerm] = useState("");
	const [selectedRange, setSelectedRange] = useState("L7");
	const [showOpportunities, setShowOpportunities] = useState(true);
	const [selectedOpportunityStatus, setSelectedOpportunityStatus] = useState("All");
	const [viewSelectedOpportunity, setViewSelectedOpportunity] = useState(null);
	const selectedOpportunities = [
		{
			_id: {
				$oid: "687f3356c22f88d1331fa579",
			},
			companyId: "aulife-healthcare-ltd",
			emailType: "LEAD_RODY",
			altEmails: ["careers@nulifeivf.com.com"],
			channels: ["EMAIL"],
			companyName: "Anulife Healthcare Ltd",
			emailId: location.state?.candidate?.email,
			jobIds: [
				{
					$oid: "687e5d4d719a0001b6ac4937",
				},
			],
			scheduledAt: {
				$date: "2025-07-22T06:44:36.293Z",
			},
			status: "success",
			templateVersion: "v1.0",
			openCount: 8,
			sentAt: {
				$date: "2025-07-22T09:22:00.800Z",
			},
			geoCity: "Mountain View",
			geoCountry: "US",
			geoRegion: "California",
			openAt: {
				$date: "2025-08-07T11:18:32.243Z",
			},
			openSource: "google-proxy",
			opened: true,
			openedFromIp: "142.250.32.5",
			uaClient: "Firefox",
			uaDevice: "desktop",
			uaOS: "Windows",
			userAgent: "Mozilla/5.0 (Windows NT 5.1; rv:11.0) Gecko Firefox/11.0 (via ggpht.com GoogleImageProxy)",
		},
	];
	const actions = [
		{
			label: "View Details",
			icon: Icons.Preview,
			onClick: (row) => {
				setViewSelectedOpportunity(row);
			},
		},
	];

	return (
		<TailwindExpertResumeLayout
			navItems={getJobsRoutes()}
			headingTitle={location.state?.candidate?.full_name}
			headingIcon="arrow-left"
		>
			<ViewOpportunityDrawer
				selectedLead={viewSelectedOpportunity}
				onClose={() => setViewSelectedOpportunity(null)}
				companyName={location.state?.candidate?.full_name || ""}
				companyLogo={location.state?.candidate?.profile_image || ""}
				headquarters={location.state?.candidate?.email || ""}
			/>
			<div className="h-full w-full overflow-hidden flex flex-col">
				<div className="grow overflow-auto p-4 md:p-6 m-3 bg-background text-foreground">
					<div className="space-y-6">
						<div className="flex items-center justify-between mb-4">
							<div className="flex space-x-2">
								{[
									{ value: "L7", label: "L7" },
									{ value: "L14", label: "L14" },
									{ value: "L28", label: "L28" },
									{ value: "L56", label: "L56" },
									{ value: "L90", label: "L90" },
								].map((option) => (
									<button
										key={option.value}
										onClick={() => setSelectedRange(option.value)}
										className={cn(
											"px-4 py-2 rounded-full text-sm font-medium transition-colors",
											selectedRange === option.value
												? "bg-primary-100 text-primary-700 border border-primary-200"
												: "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50",
										)}
									>
										{option.label}
									</button>
								))}
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							{[
								{
									label: "Total Leads",
									value: 1,
									icon: <Icons.Candidate className="w-5 h-5 text-primary-600" />,
								},
								{
									label: "L7 Emails Sent",
									value: 1,
									icon: <Icons.Mail className="w-5 h-5 text-primary-600" />,
								},
								{
									label: "L7 Emails Opened",
									value: 1,
									icon: <Icons.Preview className="w-5 h-5 text-primary-600" />,
								},
							].map((item, index) => {
								return <StatisticsCard key={index} label={item.label} value={item.value} icon={item.icon} />;
							})}
						</div>

						<div className="border border-gray-200 rounded-2xl transition-all duration-200">
							<div
								className={cn("bg-gray-50 px-4 py-3 rounded-t-2xl cursor-pointer", {
									"rounded-2xl": !showOpportunities,
								})}
								onClick={() => setShowOpportunities(!showOpportunities)}
							>
								<div className="flex items-center justify-between gap-20">
									<h6 className="font-bold text-lg">Opportunities</h6>
									{showOpportunities ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
								</div>
							</div>
							{showOpportunities && (
								<div className="mb-4 bg-white pt-4 px-4 space-y-3">
									<div className="flex items-center justify-between">
										<div className="flex space-x-2">
											{[
												{ value: "All", label: "All", count: selectedOpportunities.length },
												{
													value: "Opened",
													label: "Opened",
													count: selectedOpportunities.filter((lead) => lead.opened).length,
												},
												{
													value: "Not Opened",
													label: "Not Opened",
													count: selectedOpportunities.filter((lead) => !lead.opened).length,
												},
											].map((option) => (
												<button
													key={option.value}
													onClick={() => setSelectedOpportunityStatus(option.value)}
													className={cn(
														"px-4 py-2 rounded-full text-sm font-medium transition-colors",
														selectedOpportunityStatus === option.value
															? "bg-primary-100 text-primary-700 border border-primary-200"
															: "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50",
													)}
												>
													{option.label}
													<span className="ml-2 text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
														{option.count}
													</span>
												</button>
											))}
										</div>
										<SearchInput
											searchTerm={searchOpportunityTerm}
											onSearchChange={(value) => setSearchOpportunityTerm(value)}
											placeholder="Search opportunities..."
										/>
									</div>
									<DataTable
										columns={[
											{ header: "Email", accessor: "emailId", editable: false },
											{ header: "Email Type", accessor: "emailType", editable: false },
											{ header: "Schedualed At", accessor: "scheduledAt", editable: false },
											{ header: "Sent", accessor: "sentAt", editable: false },
											{ header: "Opened", accessor: "opened", editable: false },
											{ header: "Open At", accessor: "openAt", editable: false },
										]}
										data={selectedOpportunities
											.filter((activity) => {
												if (selectedOpportunityStatus === "Opened") {
													return activity.opened;
												}

												if (selectedOpportunityStatus === "Not Opened") {
													return !activity.opened;
												}

												return true;
											})
											.filter((activity) =>
												Object.values(activity).some((value) =>
													String(value).toLowerCase().includes(searchOpportunityTerm.toLowerCase()),
												),
											)}
										actions={actions}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</TailwindExpertResumeLayout>
	);
}

export default OpportunityDetails;
